import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Star, Loader2, CheckCircle2, Phone } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { metaLead } from "@/lib/metaPixel";
import { getAttribution } from "@/lib/attribution";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE_RE = /^[+0-9][0-9 \-/().]{5,24}$/;

export default function BeratungPflegeassistenz() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) { setError("Bitte Vor- und Nachnamen angeben."); return; }
    if (!PHONE_RE.test(form.phone.trim())) { setError("Bitte eine gültige Telefonnummer angeben (z. B. 0170 1234567)."); return; }
    if (!form.consent) { setError("Bitte stimme der Datenschutzerklärung zu."); return; }
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        audience: "pflegekraft",
        course_interest: "Ambulante Pflegeassistenz (LG1/LG2)",
        message: null,
        consent: true,
        source: "course-lp:beratung-pflegeassistenz",
        attribution: getAttribution(),
      });
      setDone(true);
      metaLead("kursseite:beratung-pflegeassistenz");
    } catch {
      setError("Anfrage konnte nicht gesendet werden – ruf uns gern direkt an: 0211 29273330.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-cream-300 bg-white px-4 py-3 text-base text-sand-800 outline-none focus:border-terra placeholder:text-sand-400";

  return (
    <div className="min-h-screen bg-cream-50 px-4 py-6 sm:py-10" data-testid="beratung-pa-page">
      <div className="max-w-md mx-auto">
        <img src="/dh_logo_dark.webp" alt="Dickies-Campus" className="h-10 mx-auto" />

        <h1 className="font-display normal-case text-[1.65rem] sm:text-3xl text-sand-800 leading-[1.15] tracking-tight text-center mt-4">
          Ohne Berufserfahrung in die Behandlungspflege
        </h1>

        <ul className="mt-3 space-y-1 text-center text-[14px] text-sand-700">
          <li>Ambulante Pflegeassistenz mit LG1 und LG2</li>
          <li>100 % online live, Start jeden Montag</li>
          <li>Mit Bildungsgutschein möglich</li>
        </ul>

        {done ? (
          <div className="mt-5 rounded-2xl bg-white border border-cream-300 shadow-soft p-6 text-center" data-testid="beratung-pa-success">
            <CheckCircle2 className="h-9 w-9 text-sage-dark mx-auto mb-2" />
            <h2 className="font-display normal-case text-xl text-sand-800">Danke für deine Anfrage!</h2>
            <p className="text-sm text-sand-600 mt-2">Wir rufen dich innerhalb von 24 Stunden zurück.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 rounded-2xl bg-white border border-cream-300 shadow-soft p-5 space-y-3" data-testid="beratung-pa-form">
            <input className={inputCls} placeholder="Vorname und Nachname *" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="beratung-pa-name" />
            <input className={inputCls} type="tel" placeholder="Telefonnummer *" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} required data-testid="beratung-pa-phone" />
            <input className={inputCls} type="email" placeholder="E-Mail *" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="beratung-pa-email" />
            <label className="flex items-start gap-2.5 text-[12px] text-sand-600 leading-snug cursor-pointer">
              <Checkbox checked={form.consent} onCheckedChange={(v) => setForm({ ...form, consent: !!v })}
                className="mt-0.5" data-testid="beratung-pa-consent" />
              <span>Ich stimme der Verarbeitung meiner Daten gemäß{" "}
                <Link to="/datenschutz" className="underline" target="_blank">Datenschutzerklärung</Link> zu. *</span>
            </label>
            {error && <p className="text-[13px] text-red-600" data-testid="beratung-pa-error">{error}</p>}
            <button type="submit" disabled={submitting} data-testid="beratung-pa-submit"
              className="w-full rounded-full bg-terra hover:bg-terra-dark text-white py-3.5 font-semibold text-[15px] inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}
              Kostenlose Beratung anfragen
            </button>
          </form>
        )}

        <div className="mt-4 text-center" data-testid="beratung-pa-trust">
          <div className="inline-flex items-center gap-1.5">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} className="h-4 w-4 text-saffron fill-saffron" />
            ))}
            <span className="text-[13px] font-semibold text-sand-800 ml-1">4,8</span>
            <span className="text-[13px] text-sand-500">/ 183 Google-Bewertungen</span>
          </div>
          <p className="text-[13px] text-sand-600 mt-1.5">Wir rufen dich innerhalb von 24 Stunden zurück.</p>
        </div>
      </div>
    </div>
  );
}
